import { useCallback, useEffect, useMemo } from 'react';
import { WatchSyncV2State } from '../api/backendAPI';
import { useAuth } from '../contexts/AuthContext';
import { useDevice } from '../contexts/DeviceContext';
import {
  getWatchSyncV2Repository,
  WatchSyncV2Repository,
} from '../services/watchSyncV2Repository';

export interface ProgressSyncInput {
  streamId: string;
  streamType: 'movie' | 'series_episode';
  position: number;
  duration: number;
}
export interface HistorySyncInput {
  contentId: string;
  contentType: 'movie' | 'series';
  name: string;
  posterUrl?: string;
  backdropUrl?: string;
  streamId: string;
  lastEpisodeStreamId?: string;
  isKids?: boolean;
}

export interface FavoriteSyncInput {
  id: string;
  type: 'live' | 'movies' | 'series';
  name: string;
  icon?: string;
  categoryId?: string;
  streamId?: string;
  seriesId?: string;
  containerExtension?: string;
  isKids?: boolean;
}

export interface UseWatchSyncResult {
  canSync: boolean;
  fetchRemote: () => Promise<WatchSyncV2State | null>;
  pushProgress: (items: ProgressSyncInput[]) => void;
  pushHistory: (items: HistorySyncInput[]) => void;
  removeProgress: (streamId: string, streamType: 'movie' | 'series_episode') => void;
  removeHistory: (contentId: string, contentType: 'movie' | 'series') => void;
  clearAll: () => Promise<void>;
  pushFavorites: (items: FavoriteSyncInput[]) => void;
  removeFavoriteRemote: (type: 'live' | 'movies' | 'series', id: string) => void;
}

function enqueueProgress(repository: WatchSyncV2Repository, item: ProgressSyncInput): void {
  const completed = item.duration > 0 && item.position >= item.duration * 0.9;
  if (completed) {
    repository.enqueue({
      type: 'progress.complete',
      payload: {
        streamId: item.streamId,
        streamType: item.streamType,
        position: item.position,
        duration: item.duration,
      },
    });
    return;
  }
  repository.enqueue({
    type: 'progress.upsert',
    payload: {
      streamId: item.streamId,
      streamType: item.streamType,
      position: item.position,
      duration: item.duration,
      completed: false,
    },
  });
}

export function useWatchSync(): UseWatchSyncResult {
  const { authData } = useAuth();
  const { deviceId, activationPassword } = useDevice();
  const profileId = authData?.profileId;
  const canSync = Boolean(profileId && deviceId && activationPassword);

  const repository = useMemo(() => {
    if (!profileId || !deviceId || !activationPassword) return null;
    return getWatchSyncV2Repository({ deviceId, activationPassword, profileId });
  }, [activationPassword, deviceId, profileId]);

  useEffect(() => {
    if (!repository) return;
    const retry = () => { void repository.syncNow().catch(() => undefined); };
    window.addEventListener('online', retry);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') retry();
    };
    document.addEventListener('visibilitychange', onVisibility);
    retry();
    return () => {
      window.removeEventListener('online', retry);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [repository]);

  const fetchRemote = useCallback(async (): Promise<WatchSyncV2State | null> => {
    if (!repository) return null;
    try {
      return await repository.syncNow();
    } catch {
      return null;
    }
  }, [repository]);

  const pushProgress = useCallback((items: ProgressSyncInput[]) => {
    if (!repository) return;
    items.forEach((item) => enqueueProgress(repository, item));
  }, [repository]);

  const pushHistory = useCallback((items: HistorySyncInput[]) => {
    if (!repository) return;
    items.forEach((item) => repository.enqueue({
      type: 'history.upsert',
      payload: item,
    }));
  }, [repository]);

  const removeProgress = useCallback((
    streamId: string,
    streamType: 'movie' | 'series_episode',
  ) => {
    repository?.enqueue({ type: 'progress.delete', payload: { streamId, streamType } });
  }, [repository]);

  const removeHistory = useCallback((
    contentId: string,
    contentType: 'movie' | 'series',
  ) => {
    repository?.enqueue({ type: 'history.delete', payload: { contentId, contentType } });
  }, [repository]);

  const clearAll = useCallback(async (): Promise<void> => {
    if (!repository || !canSync) throw new Error('Watch Sync V2 is unavailable');
    await repository.clearRemote();
  }, [canSync, repository]);

  const pushFavorites = useCallback((items: FavoriteSyncInput[]) => {
    if (!repository) return;
    items.forEach((item) => repository.enqueue({
      type: 'favorite.upsert',
      payload: {
        itemId: item.id,
        itemType: item.type,
        name: item.name,
        icon: item.icon,
        categoryId: item.categoryId,
        streamId: item.streamId,
        seriesId: item.seriesId,
        containerExtension: item.containerExtension,
        isKids: item.isKids,
      },
    }));
  }, [repository]);

  const removeFavoriteRemote = useCallback((
    type: 'live' | 'movies' | 'series',
    id: string,
  ) => {
    repository?.enqueue({ type: 'favorite.delete', payload: { itemId: id, itemType: type } });
  }, [repository]);

  return {
    canSync,
    fetchRemote,
    pushProgress,
    pushHistory,
    removeProgress,
    removeHistory,
    clearAll,
    pushFavorites,
    removeFavoriteRemote,
  };
}
